import clsx from "clsx"
import { useMemo } from "react"

import { faIcon } from "@utils/faIcon"

import { ExtraTouchArea, Skeleton } from "@particles/index"

import { Icon, IconButton, Tooltip } from "@atoms"
import { IconSize } from "@atoms/Icon/Icon.types"

import styles from "./Badge.module.scss"
import { AvatarBadgeProps, BadgeProps, IconBadgeProps } from "./Badge.types"

export default function Badge(props: BadgeProps) {
    const {
        variant = "default",
        size,

        isDismissable = false,
        onDismiss,

        showTooltip = false,

        className,
        buttonClassName,

        isLoading = false,

        children,
    } = props

    const iconSize = useMemo(() => {
        const sizeMap: { [key in BadgeProps["size"]]: IconSize } = {
            sm: 12,
            md: 14,
        }
        return sizeMap[size]
    }, [size])

    const icon: IconBadgeProps["icon"] = (props as IconBadgeProps)?.icon
    const iconClassName: IconBadgeProps["iconClassName"] = (props as IconBadgeProps)?.iconClassName
    const avatar: AvatarBadgeProps["avatar"] = (props as AvatarBadgeProps)?.avatar

    return (
        <Tooltip body={children} isDisabled={!showTooltip}>
            <div
                className={clsx(
                    styles.base,
                    styles[`${size}Size`],
                    { [styles.isDismissable]: isDismissable && !isLoading },
                    className,
                )}
            >
                <div className={styles.inner}>
                    {variant === "avatar" && (
                        <>
                            {isLoading ? (
                                <Skeleton circle={true} height={16} width={16} enableAnimation={true} />
                            ) : (
                                avatar
                            )}
                        </>
                    )}
                    {variant === "icon" && (
                        <>
                            {isLoading ? (
                                <Skeleton circle={true} height={iconSize} width={iconSize} enableAnimation={true} />
                            ) : (
                                <Icon icon={icon} size={iconSize} container="fixedHeight" className={iconClassName} />
                            )}
                        </>
                    )}
                    <div className={styles.content}>
                        {isLoading ? (
                            <Skeleton
                                height={size === "sm" ? 9 : 10}
                                width={40}
                                borderRadius={0}
                                enableAnimation={true}
                            />
                        ) : (
                            children
                        )}
                    </div>
                </div>
                {isDismissable && !isLoading && (
                    <ExtraTouchArea extraTouchArea={size === "sm" ? 4 : 8}>
                        <IconButton
                            size="xxs"
                            variant="muted"
                            icon={faIcon({ name: "xmark", style: "regular" })}
                            onClick={onDismiss}
                            className={buttonClassName}
                        />
                    </ExtraTouchArea>
                )}
            </div>
        </Tooltip>
    )
}
